import React, { type Ref } from 'react';
import { EditionAwarenessButton as Button } from '@atlassian/growth-pattern-library-edition-awareness-button';
import {
	projectTypeToCanonicalId,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types';
import { getProductOfferingKey } from '@atlassian/jira-growth-utils';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project';
import { PRODUCT_DISCOVERY, type FREE_EDITION } from '@atlassian/jira-shared-types';
import { getApplicationKeyForProject } from '@atlassian/jira-shared-types/src/application';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { EALocation } from '../../common/ui/types';
import { useShouldSeeButton } from '../../services/use-should-see-button';
import { editionAwarenessModalEntryPoint } from './entrypoint';

type SupportedApplicationKey = typeof PRODUCT_DISCOVERY;
type SupportedApplicationEdition = typeof FREE_EDITION;

const ApplicationChargeElement: Record<SupportedApplicationKey, 'user' | 'agent'> = {
	[PRODUCT_DISCOVERY]: 'user',
};

type EditionAwarenessButtonProps = {
	applicationEdition: SupportedApplicationEdition;
	applicationKey: SupportedApplicationKey;
	location: EALocation;
};

export const EditionAwarenessButton = ({
	applicationEdition,
	applicationKey,
	location,
}: EditionAwarenessButtonProps) => {
	const cloudId = useCloudId();

	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);

	const shouldSeeButton = useShouldSeeButton({ applicationEdition, applicationKey, location });

	if (!shouldSeeButton) {
		return null;
	}

	if (!projectType) {
		// Don't render the button until we know the project type as it's required for the user interaction
		return null;
	}

	const offeringKey = getProductOfferingKey(applicationKey, applicationEdition);

	if (!offeringKey) {
		// If we don't have a key then we can't render a meaningful experience to the user when the click
		// on the modal. Therefore we won't render the button to open the modal.
		return null;
	}

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={editionAwarenessModalEntryPoint}
			entryPointParams={{
				jiraApplicationKey: getApplicationKeyForProject(projectType),
				cloudId,
				productKey: projectTypeToCanonicalId(projectType),
				offeringKey,
				chargeElement: ApplicationChargeElement[applicationKey],
			}}
			entryPointProps={{
				applicationEdition,
				applicationKey,
			}}
			width={968}
			interactionName="edition-awareness-modal"
		>
			{({ ref }) => (
				// We'll replace the `Pressable` with the generic `EditionAwarenessButton`

				// This is how the examples in and tests are setup
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				<Button placeholder="" status="default" ref={ref as Ref<HTMLButtonElement>}>
					Upgrade
				</Button>
			)}
		</ModalEntryPointPressableTrigger>
	);
};
